import { useEffect, useMemo } from "react";
import { GeoJSON, useMap } from "react-leaflet";
import L, { type PathOptions } from "leaflet";
import type { Feature, FeatureCollection } from "geojson";
import FloraIQMap from "@/components/shared/FloraIQMap";
import type { Bairro, BairroFeatureProps } from "@/types/bairros";
import { colorFor, type Indicator, type Legend } from "../indicators";

interface Props {
  collection: FeatureCollection;
  byId: Map<string, Bairro>;
  indicator: Indicator;
  legend: Legend | null;
  region: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Bairros com valor medido para o indicador atual. */
  measuredIds: Set<string>;
}

const GOIANIA_CENTER: [number, number] = [-16.6869, -49.2648];

function FitToSelected({ collection, selectedId }: { collection: FeatureCollection; selectedId: string | null }) {
  const map = useMap();
  useEffect(() => {
    if (!selectedId) return;
    const feature = collection.features.find((f) => (f.properties as BairroFeatureProps | null)?.id === selectedId);
    if (!feature) return;
    const bounds = L.geoJSON(feature).getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { maxZoom: 15, padding: [40, 40] });
  }, [collection, selectedId, map]);
  return null;
}

/** Enquadra os bairros que tem dado para o indicador, para o mapa nao abrir num ponto minusculo. */
function FitToMeasured({
  collection,
  measuredIds,
  resetKey,
  enabled,
}: {
  collection: FeatureCollection;
  measuredIds: Set<string>;
  resetKey: string;
  enabled: boolean;
}) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || measuredIds.size === 0) return;
    const features = collection.features.filter((f) => measuredIds.has((f.properties as BairroFeatureProps).id));
    if (features.length === 0) return;
    const bounds = L.geoJSON({ type: "FeatureCollection", features } as FeatureCollection).getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { maxZoom: 14, padding: [30, 30] });
    // Reenquadra so quando muda o indicador ou a regiao, nao a cada selecao.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, collection, map, enabled]);
  return null;
}

export default function BairrosMap({ collection, byId, indicator, legend, region, selectedId, onSelect, measuredIds }: Props) {
  // Recria a camada quando muda o que ela mostra; o Leaflet nao reestiliza sozinho.
  const layerKey = `${indicator.id}|${region ?? "all"}|${selectedId ?? ""}`;

  const style = useMemo(
    () =>
      (feature?: Feature): PathOptions => {
        const props = feature?.properties as BairroFeatureProps | undefined;
        const bairro = props ? byId.get(props.id) : undefined;
        const inRegion = !region || props?.region === region;
        const value = bairro ? indicator.value(bairro) : null;
        const selected = props?.id === selectedId;
        const measured = value != null && legend != null;
        return {
          // Bairro sem dado fica quase transparente: o mapa mostra onde HA informacao.
          fillColor: measured ? colorFor(value as number, legend as Legend) : "#9AA3A8",
          fillOpacity: !inRegion ? 0.03 : measured ? 0.72 : 0.06,
          color: selected ? "#0B3D2A" : "#5F6B73",
          weight: selected ? 2.5 : 0.5,
          opacity: !inRegion ? 0.25 : 0.9,
        };
      },
    [byId, indicator, legend, region, selectedId],
  );

  return (
    <FloraIQMap center={GOIANIA_CENTER} zoom={11} minZoom={9} className="h-full w-full" defaultBaseLayer="roadmap">
      <GeoJSON
        key={layerKey}
        data={collection}
        style={style}
        onEachFeature={(feature, layer) => {
          const props = feature.properties as BairroFeatureProps;
          const bairro = byId.get(props.id);
          const value = bairro ? indicator.value(bairro) : null;
          layer.bindTooltip(
            `<strong>${props.name}</strong><br/>${props.region ?? "Região não classificada"}<br/>${
              value != null ? `${indicator.label}: ${indicator.format(value)}` : "Sem dado para este indicador"
            }`,
            { sticky: true },
          );
          layer.on("click", () => onSelect(props.id));
        }}
      />
      <FitToSelected collection={collection} selectedId={selectedId} />
      <FitToMeasured
        collection={collection}
        measuredIds={measuredIds}
        resetKey={`${indicator.id}|${region ?? "all"}`}
        enabled={selectedId == null}
      />
    </FloraIQMap>
  );
}
